/**
 * Tiered free gifts — three tiers, driven from theme settings:
 *
 *   Tier 1  every order        -> settings.fg_t1_product auto-added (qty 1)
 *   Tier 2  >= fg_t2_threshold -> ONE premium pick: Duffle OR The Leather Set
 *   Tier 3  >= fg_t3_threshold -> TWO premium picks total (duplicates allowed)
 *
 * Gift lines carry line item properties:
 *   _free_gift: 'tier1' | 'premium'
 *   _fg_pick:   'duffle' | 'set'      (premium picks only)
 *   _fg_slot:   '1' | '2'             (premium picks only)
 *
 * Every premium pick is its own cart line at quantity 1 — the _fg_slot
 * property stops Shopify merging two picks of the same variant into one
 * qty-2 line. This is REQUIRED by the discount setup: the two automatic
 * Buy X Get Y rules (>=£575 and >=£800) each discount one line, and on
 * non-Plus plans a line discounted by one rule is ineligible for the
 * second — so two picks of the same gift must be two separate lines.
 *
 * The Leather Set is a single bundled product (one variant), so a set
 * pick is one line like any other.
 *
 * This script:
 *   - auto-adds the tier-1 gift when the cart has any paid item
 *     (respects a per-session opt-out if the customer removes it)
 *   - adds / removes / swaps premium pick lines
 *   - removes picks automatically when the cart drops below a tier
 *   - keeps the gift state legal (qty clamps, slot dedupe)
 *   - converts base-currency thresholds with Shopify's live rate and
 *     stores them in cart attributes (_fg_thr2/_fg_thr3/_fg_thr_ccy) so
 *     server-rendered Liquid is exact in every market currency
 *   - live-updates announcement-bar amounts ([data-gift-amt])
 *
 * The £0 price itself is enforced server-side by Shopify automatic
 * discounts (Buy X Get Y rules) — this script only manages which gift
 * lines are in the cart. Only lines tagged _free_gift are ever touched.
 */
(function () {
  if (window.freeGiftLoaded) return;
  window.freeGiftLoaded = true;

  var busy = false;
  var lastT1Present = null;
  var T1_OPTOUT_KEY = 'fg_t1_optout';

  function cfg() {
    var el = document.querySelector('[data-free-gift]');
    if (!el) return null;
    return {
      el: el,
      t2: parseInt(el.getAttribute('data-gift-t2'), 10) || 0,
      t3: parseInt(el.getAttribute('data-gift-t3'), 10) || 0,
      fx: el.getAttribute('data-gift-fx'),
      t2Base: parseFloat(el.getAttribute('data-gift-t2-base')) || 0,
      t3Base: parseFloat(el.getAttribute('data-gift-t3-base')) || 0,
      shopCurrency: el.getAttribute('data-shop-currency'),
      t1Variant: parseInt(el.getAttribute('data-t1-variant'), 10) || 0,
      // Tier-1 minimum (base currency, e.g. 100 = £100) — the gift's discount
      // has the same minimum; below it the gift would be charged, so don't add.
      t1MinBase: parseFloat(el.getAttribute('data-t1-min-base')) || 0,
      // Trigger mode: the tier-1 gift is added only while one of these
      // products is in the cart (e.g. Canvas Bag -> free Luggage Tag).
      t1Triggers: (el.getAttribute('data-t1-triggers') || '').split(',').filter(Boolean),
      // Forced: removing the gift doesn't opt out — it comes back.
      t1Force: el.getAttribute('data-t1-force') === '1',
      // Gift variant has no stock -> don't try to add it (would 422).
      t1Available: el.getAttribute('data-t1-available') !== 'false',
      // Premium picks resolve to TIER-SCOPED hidden gift SKUs: the >=t2 and
      // >=t3 automatic discounts each target only their own tier's products,
      // which is what stops the two rules stacking into a third gift.
      variants: {
        duffle: {
          2: parseInt(el.getAttribute('data-duffle-t2-variant'), 10) || 0,
          3: parseInt(el.getAttribute('data-duffle-t3-variant'), 10) || 0,
        },
        set: {
          2: parseInt(el.getAttribute('data-set-t2-variant'), 10) || 0,
          3: parseInt(el.getAttribute('data-set-t3-variant'), 10) || 0,
        },
      },
      giftHandles: (el.getAttribute('data-gift-handles') || '').split(',').filter(Boolean),
      // Payday per-set mode (set by cart-drawer.liquid only while Payday is
      // on): allowance = number of sets in the cart, max 2.
      perSet: el.getAttribute('data-gift-per-set') === '1',
      setHandles: (el.getAttribute('data-gift-set-handles') || '').split(',').filter(Boolean),
      // Per-set mode: gift auto-added for every set ('set' = Leather Set) so
      // nobody can check out without it; the customer can swap to Duffle.
      autoPick: el.getAttribute('data-gift-auto-pick') || '',
      // Sets that already contain a Duffle (Complete Journey): their gift is
      // Leather Set only, so they don't count toward Duffle picks.
      leatherOnly: (el.getAttribute('data-gift-leather-only') || '').split(',').filter(Boolean),
    };
  }

  function t1OptedOut() {
    try { return sessionStorage.getItem(T1_OPTOUT_KEY) === '1'; } catch (e) { return false; }
  }
  function setT1OptOut() {
    try { sessionStorage.setItem(T1_OPTOUT_KEY, '1'); } catch (e) { /* noop */ }
  }
  function clearT1OptOut() {
    try { sessionStorage.removeItem(T1_OPTOUT_KEY); } catch (e) { /* noop */ }
  }

  /* ---- cart state ---- */
  function readState(cart, c) {
    var gifts = [];
    var countableTotal = cart.total_price;
    var nonGiftCount = 0; // untagged lines (any product)
    var countableCount = 0; // lines that count toward thresholds
    cart.items.forEach(function (item) {
      var isTagged = item.properties && item.properties._free_gift;
      // Gift PRODUCTS never count toward thresholds, tagged or not —
      // mirrors the discount rules' "customer buys" collection scope.
      var isGiftProduct = c.giftHandles.indexOf(item.handle) !== -1;
      if (isTagged) gifts.push(item);
      else nonGiftCount += 1;
      if (isTagged || isGiftProduct) {
        countableTotal -= item.final_line_price;
      } else {
        countableCount += 1;
      }
    });
    var t1Line = gifts.find(function (g) { return g.properties._free_gift === 'tier1'; });
    var picks = gifts.filter(function (g) { return g.properties._fg_pick; });
    var allowance = 0;
    var maxDuffle = 2;
    if (c.perSet) {
      var setCount = 0;
      var duffleSets = 0;
      cart.items.forEach(function (item) {
        if (item.properties && item.properties._free_gift) return;
        if (c.setHandles.indexOf(item.handle) !== -1) {
          setCount += item.quantity;
          if (c.leatherOnly.indexOf(item.handle) === -1) duffleSets += item.quantity;
        }
      });
      allowance = Math.min(2, setCount);
      maxDuffle = Math.min(allowance, duffleSets);
    } else if (countableCount > 0) {
      if (c.t3 > 0 && countableTotal >= c.t3) allowance = 2;
      else if (c.t2 > 0 && countableTotal >= c.t2) allowance = 1;
    }
    var rate = parseFloat((window.Shopify && Shopify.currency && Shopify.currency.rate) || '1') || 1;
    var t1MinCents = Math.round(c.t1MinBase * rate * 100);
    var t1Ok = countableCount > 0 && countableTotal >= t1MinCents;
    if (c.t1Triggers.length) {
      t1Ok = cart.items.some(function (item) {
        return !(item.properties && item.properties._free_gift) && c.t1Triggers.indexOf(item.handle) !== -1;
      });
    }
    return {
      t1Ok: t1Ok,
      gifts: gifts,
      nonGiftCount: nonGiftCount,
      countableCount: countableCount,
      t1Line: t1Line,
      picks: picks,
      allowance: allowance,
      maxDuffle: maxDuffle,
    };
  }

  function picksOf(state, option) {
    return state.picks.filter(function (p) { return p.properties._fg_pick === option; });
  }

  function freeSlot(state) {
    var used = state.picks.map(function (p) { return p.properties._fg_slot; });
    if (used.indexOf('1') === -1) return '1';
    if (used.indexOf('2') === -1) return '2';
    return null;
  }

  /* ---- cart plumbing ---- */
  function changeLine(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    });
  }

  function addItems(items) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    });
  }

  /**
   * Which tier SKU a pick should use, given how many picks the cart will
   * hold. CRITICAL: the >=t3 discount is "get QUANTITY 2 free" and Shopify
   * only applies it when BOTH items are present — a lone pick at tier 3
   * would go un-discounted. So a single pick always rides the T2 SKU
   * (covered by the >=t2 rule, whose minimum is met a fortiori) and picks
   * migrate to T3 SKUs only when there are two of them.
   */
  function tierFor(state, totalPicks) {
    return state.allowance >= 2 && totalPicks >= 2 ? 3 : 2;
  }

  function pickItem(c, option, slot, tier) {
    return {
      id: c.variants[option][tier],
      quantity: 1,
      properties: { _free_gift: 'premium', _fg_pick: option, _fg_slot: slot, _fg_tier: String(tier) },
    };
  }

  function refreshCartDrawer() {
    return fetch('/?section_id=cart-drawer')
      .then(function (res) { return res.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var newDrawer = doc.querySelector('cart-drawer');
        var oldDrawer = document.querySelector('cart-drawer');
        if (newDrawer && oldDrawer) oldDrawer.innerHTML = newDrawer.innerHTML;
      });
  }

  function updateCartCount(count) {
    document.querySelectorAll('.cart-count-bubble').forEach(function (el) {
      el.textContent = count;
    });
  }

  function afterMutation() {
    return fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        updateCartCount(cart.item_count);
        // The /cart page is server-rendered — reload it so it shows the gift.
        if (/^\/cart\/?$/.test(window.location.pathname)) { window.location.reload(); return null; }
        return refreshCartDrawer();
      });
  }

  function chain(ops) {
    return ops.reduce(function (p, op) { return p.then(op); }, Promise.resolve());
  }

  /* ---- cleanup: keep the cart state legal ---- */
  function checkGift() {
    var c = cfg();
    if (!c || busy) return;
    busy = true;

    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var state = readState(cart, c);
        var ops = [];

        // Trigger mode (e.g. Canvas Bag -> Mystery Free Gift): the gift variant
        // may only exist as OUR tagged line while a trigger product is in the
        // cart. Any untagged copy (added via /cart/add, a link, an app) goes,
        // and without a trigger every copy goes.
        if (c.t1Triggers.length && c.t1Variant) {
          var strays = cart.items.filter(function (item) {
            if (item.variant_id !== c.t1Variant) return false;
            var tagged = item.properties && item.properties._free_gift === 'tier1';
            return !state.t1Ok || !tagged;
          });
          if (strays.length) {
            var upd = {};
            strays.forEach(function (item) { upd[item.key] = 0; });
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ updates: upd }),
            }).then(afterMutation).then(function () {
              // Run again once we're no longer busy, so the proper tagged gift
              // is added right away when a trigger product is in the cart.
              setTimeout(checkGift, 300);
            });
          }
        }

        // Cart holds only gifts -> everything goes.
        if (state.nonGiftCount === 0) {
          if (!state.gifts.length) { lastT1Present = false; return null; }
          lastT1Present = false;
          clearT1OptOut();
          return chain(state.gifts.map(function (g) {
            return function () { return changeLine(g.key, 0); };
          })).then(afterMutation);
        }

        // Tier 1 — detect a customer removing it, otherwise auto-add.
        // Only offered when at least one countable (non-gift-product) line
        // exists, since the tag discount's own minimum requires that too.
        if (state.t1Line) {
          lastT1Present = true;
          if (!state.t1Ok) {
            ops.push(function () { return changeLine(state.t1Line.key, 0); });
            lastT1Present = false;
          } else if (c.t1Variant && state.t1Line.variant_id !== c.t1Variant) {
            // Legacy tag line (real product) -> replace with the gift SKU.
            ops.push(function () { return changeLine(state.t1Line.key, 0); });
            ops.push(function () {
              return addItems([{ id: c.t1Variant, quantity: 1, properties: { _free_gift: 'tier1' } }]);
            });
          } else if (state.t1Line.quantity !== 1) {
            ops.push(function () { return changeLine(state.t1Line.key, 1); });
          }
        } else {
          if (lastT1Present === true && !c.t1Force) setT1OptOut(); // they removed it — respect that (unless forced)
          lastT1Present = false;
          if (c.t1Variant && c.t1Available && (c.t1Force || !t1OptedOut()) && state.t1Ok) {
            ops.push(function () {
              return addItems([{ id: c.t1Variant, quantity: 1, properties: { _free_gift: 'tier1' } }]);
            });
          }
        }

        // Premium pick lines: clamp each to qty 1, de-dupe slots, trim
        // over-allowance (newest slots first), and MIGRATE picks whose
        // variant belongs to the wrong tier — the discounts only free the
        // right tier's hidden SKUs (see tierFor).
        var effectivePicks = Math.min(state.picks.length, state.allowance);
        var tier = tierFor(state, effectivePicks);
        var seenSlots = {};
        var keep = [];
        state.picks.forEach(function (p) {
          var slot = p.properties._fg_slot || '1';
          if (seenSlots[slot]) {
            ops.push(function () { return changeLine(p.key, 0); }); // duplicate slot
          } else {
            seenSlots[slot] = true;
            keep.push(p);
          }
        });
        keep.sort(function (a, b) {
          return (a.properties._fg_slot || '1') < (b.properties._fg_slot || '1') ? -1 : 1;
        });
        keep.forEach(function (p, idx) {
          if (idx >= state.allowance) {
            ops.push(function () { return changeLine(p.key, 0); }); // over allowance
            return;
          }
          var option = p.properties._fg_pick;
          var want = c.variants[option] && c.variants[option][tier];
          if (want && p.variant_id !== want) {
            // Wrong tier (or legacy variant) -> replace, keeping pick + slot.
            var slot = p.properties._fg_slot || String(idx + 1);
            ops.push(function () { return changeLine(p.key, 0); });
            ops.push(function () { return addItems([pickItem(c, option, slot, tier)]); });
          } else if (p.quantity !== 1) {
            ops.push(function () { return changeLine(p.key, 1); });
          }
        });

        // Per-set mode: one gift per set, always. Cap Duffle picks (Complete
        // Journey = Leather Set only) and top up empty slots with autoPick.
        if (c.perSet && c.autoPick) {
          var kept = keep.slice(0, state.allowance);
          var topTier = tierFor(state, state.allowance);
          var duffles = kept.filter(function (p) { return p.properties._fg_pick === 'duffle'; });
          var excess = duffles.length - state.maxDuffle;
          if (excess > 0) {
            duffles.slice(duffles.length - excess).forEach(function (p) {
              var sl = p.properties._fg_slot || '1';
              ops.push(function () { return changeLine(p.key, 0); });
              ops.push(function () { return addItems([pickItem(c, 'set', sl, topTier)]); });
            });
          }
          var usedSlots = kept.map(function (p) { return p.properties._fg_slot || '1'; });
          for (var n = kept.length; n < state.allowance; n++) {
            var free = ['1', '2'].filter(function (x) { return usedSlots.indexOf(x) === -1; })[0];
            if (!free) break;
            usedSlots.push(free);
            (function (sl) {
              ops.push(function () { return addItems([pickItem(c, c.autoPick, sl, topTier)]); });
            })(free);
          }
        }

        if (!ops.length) return null;
        return chain(ops).then(afterMutation);
      })
      .catch(function (e) { console.error('[free-gift]', e); })
      .finally(function () { busy = false; });
  }

  /* ---- picking ---- */
  function handlePick(option, delta) {
    var c = cfg();
    if (!c || busy) return;
    busy = true;

    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var state = readState(cart, c);
        if (!state.allowance) return null;

        var other = option === 'duffle' ? 'set' : 'duffle';
        var mine = picksOf(state, option);
        var theirs = picksOf(state, other);
        var ops = [];
        if (delta > 0 && option === 'duffle' && mine.length >= state.maxDuffle) return null; // Leather Set only

        if (delta < 0) {
          // Explicit remove control: drop this option's newest pick.
          // (checkGift migrates the remaining pick back to the T2 SKU.)
          if (!mine.length) return null;
          var newest = mine[mine.length - 1];
          ops.push(function () { return changeLine(newest.key, 0); });
        } else if (state.picks.length < state.allowance) {
          // Room for one more pick — tier reflects the resulting count.
          // (checkGift migrates the existing pick to match.)
          if (state.allowance === 1 && mine.length > 0) return null; // already selected
          var slot = freeSlot(state);
          if (!slot) return null;
          var addTier = tierFor(state, state.picks.length + 1);
          ops.push(function () { return addItems([pickItem(c, option, slot, addTier)]); });
        } else if (state.allowance === 1 && mine.length > 0) {
          // Toggle off.
          ops.push(function () { return changeLine(mine[0].key, 0); });
        } else if (!mine.length && theirs.length) {
          // Full with the other option -> swap one over, reusing its slot.
          var swapTier = tierFor(state, state.picks.length);
          var swapped = theirs[theirs.length - 1];
          var slot2 = swapped.properties._fg_slot || '1';
          ops.push(function () { return changeLine(swapped.key, 0); });
          ops.push(function () { return addItems([pickItem(c, option, slot2, swapTier)]); });
        } else {
          return null; // full with this option — use the remove control
        }

        return chain(ops).then(afterMutation);
      })
      .catch(function (e) { console.error('[free-gift]', e); })
      // The fetch hook's checkGift fired while we were busy and bailed —
      // run it now so a first pick migrates to the T3 SKU straight away.
      .finally(function () { busy = false; setTimeout(checkGift, 0); });
  }

  function addTier1Back() {
    var c = cfg();
    if (!c || busy || !c.t1Variant) return;
    busy = true;
    clearT1OptOut();
    addItems([{ id: c.t1Variant, quantity: 1, properties: { _free_gift: 'tier1' } }])
      .then(afterMutation)
      .catch(function (e) { console.error('[free-gift]', e); })
      .finally(function () { busy = false; lastT1Present = true; });
  }

  /* ---- currency conversion ---- */
  var fxAttempts = 0;
  function syncFx() {
    var c = cfg();
    if (!c || c.fx !== 'auto' || busy || fxAttempts > 4) return;
    if (!(window.Shopify && Shopify.currency && Shopify.currency.rate && Shopify.currency.active)) return;

    var active = Shopify.currency.active;
    if (!c.shopCurrency || active === c.shopCurrency) return;

    var rate = parseFloat(Shopify.currency.rate);
    if (!rate || !c.t2Base || !c.t3Base) return;

    var want2 = Math.round(c.t2Base * rate) * 100;
    var want3 = Math.round(c.t3Base * rate) * 100;
    if (c.t2 === want2 && c.t3 === want3) return;

    fxAttempts += 1;
    busy = true;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: { _fg_thr2: String(want2), _fg_thr3: String(want3), _fg_thr_ccy: active },
      }),
    })
      .then(afterMutation)
      .catch(function (e) { console.error('[free-gift fx]', e); })
      .finally(function () { busy = false; });
  }

  /* ---- announcement bar amounts ---- */
  function updateAnnouncementAmounts() {
    var els = document.querySelectorAll('[data-gift-amt]');
    if (!els.length) return;
    if (!(window.Shopify && Shopify.currency && Shopify.currency.active)) return;
    var active = Shopify.currency.active;
    var shopCurrency = els[0].getAttribute('data-gift-amt-shop') || '';
    if (active === shopCurrency) return;
    var rate = parseFloat(Shopify.currency.rate);
    if (!rate) return;
    els.forEach(function (el) {
      var base = parseFloat(el.getAttribute('data-gift-amt-base'));
      if (!base) return;
      var amount = Math.round(base * rate);
      try {
        el.textContent = new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: active,
          maximumFractionDigits: 0,
        }).format(amount);
      } catch (e) {
        el.textContent = amount + ' ' + active;
      }
    });
  }

  /* ---- events ---- */
  document.addEventListener('click', function (e) {
    var minus = e.target.closest('[data-gift-remove]');
    if (minus) {
      e.preventDefault();
      handlePick(minus.getAttribute('data-gift-option'), -1);
      return;
    }
    var t1 = e.target.closest('[data-gift-t1-add]');
    if (t1) {
      e.preventDefault();
      addTier1Back();
      return;
    }
    var btn = e.target.closest('[data-gift-pick]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    handlePick(btn.getAttribute('data-gift-option'), 1);
  });

  // Re-check after any cart mutation made anywhere in the theme.
  var origFetch = window.fetch;
  window.fetch = function () {
    var url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
    var result = origFetch.apply(this, arguments);
    if (/\/cart\/(add|change|update|clear)/.test(url)) {
      result.then(function () { setTimeout(checkGift, 150); }).catch(function () {});
    }
    return result;
  };

  document.addEventListener('cart:updated', function () {
    setTimeout(checkGift, 150);
    setTimeout(syncFx, 400);
  });

  function init() {
    setTimeout(syncFx, 200);
    setTimeout(checkGift, 400);
    setTimeout(updateAnnouncementAmounts, 250);
  }
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
